import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Book, Leaf, Store } from 'lucide-react';

interface RelatedItem {
  id: string;
  title?: string;
  name?: string;
  description?: string;
  url: string;
  type: 'recipe' | 'blog' | 'produce' | 'farm' | 'seasonal';
  image?: string;
  meta?: string; // e.g., "5 min read", "Easy", "In Season"
}

interface RelatedContentWidgetProps {
  items: RelatedItem[];
  title?: string;
  contentType?: string;
  maxItems?: number;
  layout?: 'list' | 'grid';
}

const RelatedContentWidget: React.FC<RelatedContentWidgetProps> = ({
  items,
  title = 'Related Content',
  contentType,
  maxItems = 6,
  layout = 'list',
}) => {
  const displayItems = items.slice(0, maxItems);

  const getIcon = (type: string) => {
    switch (type) {
      case 'recipe':
        return <Book size={16} />;
      case 'blog':
        return <Book size={16} />;
      case 'produce':
        return <Leaf size={16} />;
      case 'farm':
        return <Store size={16} />;
      case 'seasonal':
        return <Calendar size={16} />;
      default:
        return <Book size={16} />;
    }
  };

  const getTypeLabel = (type: string): string => {
    switch (type) {
      case 'recipe':
        return 'Recipe';
      case 'blog':
        return 'Article';
      case 'produce':
        return 'Produce';
      case 'farm':
        return 'Farm';
      case 'seasonal':
        return 'Seasonal Guide';
      default:
        return 'Content';
    }
  };

  const styles = {
    widget: {
      backgroundColor: '#fff',
      borderRadius: '8px',
      padding: '24px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      marginBottom: '24px',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '20px',
      paddingBottom: '12px',
      borderBottom: '2px solid #2D5A27',
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
    },
    headerIcon: {
      color: '#2D5A27',
      marginRight: '12px',
    },
    title: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#1a1a1a',
      margin: 0,
    },
    subtitle: {
      fontSize: '13px',
      color: '#666',
      marginTop: '4px',
    },
    listContainer: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '12px',
    },
    gridContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
      gap: '12px',
    },
    itemCard: {
      backgroundColor: '#f9f9f9',
      borderRadius: '6px',
      padding: '12px',
      border: '1px solid #e5e5e5',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    } as React.CSSProperties,
    itemCardGrid: {
      flexDirection: 'column' as const,
      alignItems: 'flex-start',
      minHeight: '120px',
    },
    itemCardHover: {
      backgroundColor: '#f0f7ef',
      borderColor: '#2D5A27',
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
    },
    itemImage: {
      width: '60px',
      height: '60px',
      borderRadius: '4px',
      objectFit: 'cover' as const,
      backgroundColor: '#e5e5e5',
    },
    itemImageGrid: {
      width: '100%',
      height: '80px',
    },
    itemContent: {
      flex: 1,
    },
    itemContentGrid: {
      width: '100%',
    },
    itemType: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      fontSize: '11px',
      fontWeight: '600',
      color: '#2D5A27',
      textTransform: 'uppercase' as const,
      marginBottom: '4px',
    },
    itemTitle: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#1a1a1a',
      marginBottom: '4px',
      lineHeight: '1.3',
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical' as const,
      overflow: 'hidden',
    },
    itemMeta: {
      fontSize: '12px',
      color: '#666',
    },
    arrow: {
      color: '#2D5A27',
      opacity: 0.5,
      transition: 'opacity 0.3s ease',
      flexShrink: 0,
    },
    empty: {
      textAlign: 'center' as const,
      padding: '24px',
      color: '#666',
      backgroundColor: '#f9f9f9',
      borderRadius: '6px',
    },
  };

  if (!displayItems || displayItems.length === 0) {
    return (
      <div style={styles.widget}>
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <Book style={styles.headerIcon} size={24} />
            <div>
              <h3 style={styles.title}>{title}</h3>
            </div>
          </div>
        </div>
        <div style={styles.empty}>
          <p>No related content available yet.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.widget}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <Book style={styles.headerIcon} size={24} />
          <div>
            <h3 style={styles.title}>{title}</h3>
            {contentType && (
              <p style={styles.subtitle}>
                {displayItems.length} {contentType}
                {displayItems.length !== 1 ? 's' : ''}
              </p>
            )}
          </div>
        </div>
      </div>

      <div style={layout === 'grid' ? styles.gridContainer : styles.listContainer}>
        {displayItems.map((item) => {
          const itemName = item.title || item.name || 'Untitled';
          const isGrid = layout === 'grid';

          return (
            <Link
              key={item.id}
              to={item.url}
              style={{ textDecoration: 'none' }}
            >
              <div
                style={{
                  ...styles.itemCard,
                  ...(isGrid ? styles.itemCardGrid : {}),
                }}
                onMouseEnter={(e) => {
                  Object.assign(e.currentTarget.style, styles.itemCardHover);
                  const arrow = e.currentTarget.querySelector('.arrow-icon') as HTMLElement;
                  if (arrow) arrow.style.opacity = '1';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#f9f9f9';
                  e.currentTarget.style.borderColor = '#e5e5e5';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                  const arrow = e.currentTarget.querySelector('.arrow-icon') as HTMLElement;
                  if (arrow) arrow.style.opacity = '0.5';
                }}
              >
                {item.image && (
                  <img
                    src={item.image}
                    alt={itemName}
                    style={{
                      ...styles.itemImage,
                      ...(isGrid ? styles.itemImageGrid : {}),
                    }}
                  />
                )}

                <div
                  style={{
                    ...styles.itemContent,
                    ...(isGrid ? styles.itemContentGrid : {}),
                  }}
                >
                  <div style={styles.itemType}>
                    {getIcon(item.type)}
                    {getTypeLabel(item.type)}
                  </div>

                  <div style={styles.itemTitle}>{itemName}</div>

                  {item.meta && <div style={styles.itemMeta}>{item.meta}</div>}

                  {item.description && !isGrid && (
                    <div
                      style={{
                        ...styles.itemMeta,
                        marginTop: '4px',
                        display: '-webkit-box',
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: 'vertical' as const,
                        overflow: 'hidden',
                      }}
                    >
                      {item.description}
                    </div>
                  )}
                </div>

                {!isGrid && (
                  <div className="arrow-icon" style={styles.arrow}>
                    <ArrowRight size={18} />
                  </div>
                )}
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default RelatedContentWidget;
